import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../LocalizedLink/LocalizedLink";
import BlogSidebar from "../BlogSidebar/BlogSidebar.js";
import ArticleContent from "../ArticleBlocks/ArticleBlocks";
import { useBlog } from "../../config/BlogProvider";
import { useLanguage } from "../../context/LanguageContext";
import { ikUrl } from "../../utils/imagekit";
import {
  getTranslation,
  availableLangs,
  estimateReadingTime,
  relatedArticles,
  formatDate,
  dirFor,
} from "../../utils/article";

const ClickHandler = () => {
  window.scrollTo(10, 0);
};

const BlogSingle = ({ article }) => {
  const { articles } = useBlog();
  const { lang, setLang } = useLanguage();
  const { t } = useTranslation("blog");

  const picked = getTranslation(article, lang);
  if (!picked) return null;
  const { lang: shownLang, tr } = picked;
  const dir = dirFor(shownLang);

  const langs = availableLangs(article);
  const related = relatedArticles(articles, article, lang, 3);
  const cover = article.coverImage
    ? ikUrl(article.coverImage.url || article.coverImage.path, "w-1200,q-80,f-auto")
    : "";

  const seo = tr.seo || {};
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = encodeURIComponent(tr.title || "");
  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}`,
    x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      shareUrl
    )}&text=${shareTitle}`,
    whatsapp: `https://api.whatsapp.com/send?text=${shareTitle}%20${encodeURIComponent(
      shareUrl
    )}`,
  };

  return (
    <section className="wpo-blog-single-section section-padding">
      <Helmet>
        <html lang={shownLang} dir={dir} />
        <title>{seo.title || tr.title}</title>
        <meta name="description" content={seo.description || tr.excerpt || ""} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seo.title || tr.title} />
        <meta
          property="og:description"
          content={seo.description || tr.excerpt || ""}
        />
        {cover && <meta property="og:image" content={cover} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title || tr.title} />
        <meta
          name="twitter:description"
          content={seo.description || tr.excerpt || ""}
        />
        {cover && <meta name="twitter:image" content={cover} />}
      </Helmet>

      <div className="container">
        <div className="row">
          <div className="col col-lg-8 col-12">
            <div className="wpo-blog-content">
              <div className="post format-standard-image">
                {cover && (
                  <div className="entry-media">
                    <img src={cover} alt={article.coverImage.alt || tr.title} />
                  </div>
                )}

                <div className="entry-meta">
                  <ul>
                    <li>
                      <i className="fi flaticon-user"></i> {article.author}
                    </li>
                    <li>
                      <i className="fi flaticon-calendar"></i>{" "}
                      {formatDate(article.publishedAt, shownLang)}
                    </li>
                    <li>{t("readingTime", { count: estimateReadingTime(tr) })}</li>
                    {article.category && (
                      <li>
                        {t(`cat.${article.category}`, {
                          defaultValue: article.category,
                        })}
                      </li>
                    )}
                  </ul>
                </div>

                {/* Language switcher */}
                {langs.length > 1 && (
                  <div className="lang-switcher">
                    {langs.map((l) => (
                      <button
                        key={l}
                        type="button"
                        className={l === shownLang ? "active" : ""}
                        onClick={() => setLang(l)}
                      >
                        {l.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}

                <h1>{tr.title}</h1>

                <ArticleContent blocks={tr.content} lang={shownLang} dir={dir} />

                {/* Share */}
                <div className="tag-share-s2 clearfix">
                  <div className="tag">
                    <span>{t("share")} </span>
                    <ul>
                      <li>
                        <a href={shareLinks.facebook} target="_blank" rel="noreferrer">
                          facebook
                        </a>
                      </li>
                      <li>
                        <a href={shareLinks.x} target="_blank" rel="noreferrer">
                          x
                        </a>
                      </li>
                      <li>
                        <a href={shareLinks.whatsapp} target="_blank" rel="noreferrer">
                          whatsapp
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>

                {article.author && (
                  <div className="author-box">
                    <div className="author-content">
                      <span className="author-name">
                        {t("by")} {article.author}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Related articles */}
              {related.length > 0 && (
                <div className="related-posts">
                  <h3>{t("related")}</h3>
                  <div className="row">
                    {related.map((r) => {
                      const rp = getTranslation(r, lang);
                      if (!rp) return null;
                      return (
                        <div className="col col-md-4 col-12" key={r.id}>
                          <div className="post">
                            {r.coverImage && (
                              <div className="img-holder">
                                <LocalizedLink
                                  onClick={ClickHandler}
                                  to={`/blog/${rp.tr.slug}`}
                                >
                                  <img
                                    src={ikUrl(
                                      r.coverImage.url || r.coverImage.path,
                                      "w-400,q-75,f-auto"
                                    )}
                                    alt={r.coverImage.alt || rp.tr.title}
                                    loading="lazy"
                                  />
                                </LocalizedLink>
                              </div>
                            )}
                            <h4>
                              <LocalizedLink
                                onClick={ClickHandler}
                                to={`/blog/${rp.tr.slug}`}
                              >
                                {rp.tr.title}
                              </LocalizedLink>
                            </h4>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <BlogSidebar />
        </div>
      </div>
    </section>
  );
};

export default BlogSingle;
